import { FC, Fragment, useCallback, useEffect, useRef, useState } from "react"

import { yupResolver } from "@hookform/resolvers/yup"
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  debounce,
  Stack,
  TextField as MuiTextField,
} from "@mui/material"
import { useMutationUpdateGroup } from "api/reactQuery/mutations/groups"
import {
  useInfiniteQueryGroupCandidates,
  useInfiniteQueryGroupMembers,
} from "api/reactQuery/queries/groups"
import Dialog from "components/common/dialog/dialog"
import TextField from "components/form/common/field/textField"
import { useCustomSnackbar } from "hooks/snackbar"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { IGroup } from "ts/interfaces/Group"
import { IObjectStudentDetailed } from "ts/interfaces/Student"
import { requiredStringSchema } from "utils/yup"
import * as yup from "yup"

import { getGroupMutationFeedback } from "./getGroupMutationFeedback"
import { renderGroupMemberTags } from "./renderGroupMemberTags"
import { useGroupMembersListboxMaxHeight } from "./useGroupMembersListboxMaxHeight"

// Structurally satisfied by both IGroupMember (initial value) and IObjectStudentDetailed
// (search options) so the same Autocomplete can mix pre-selected members with search
// results without converting either shape.
type IMemberOption = Pick<
  IObjectStudentDetailed,
  "id" | "first_name" | "last_name"
>

interface IFormValues {
  name: string
  members: IMemberOption[]
}

const schema = yup.object({
  name: requiredStringSchema,
  members: yup.array().default([]),
})

interface IProps {
  open: boolean
  classId: number
  group: IGroup
  onClose: () => void
}

const EditGroupDialog: FC<IProps> = ({ open, classId, group, onClose }) => {
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const [search, setSearch] = useState("")
  const [isMembersFocused, setIsMembersFocused] = useState(false)
  const {
    listboxMaxHeight,
    onOpen: onListboxOpen,
    onClose: onListboxClose,
  } = useGroupMembersListboxMaxHeight()

  const defaultValues: IFormValues = {
    name: group.name,
    members: group.members_preview.map((member) => ({
      id: member.id,
      first_name: member.first_name ?? "",
      last_name: member.last_name ?? "",
    })),
  }

  const methods = useForm<IFormValues>({
    resolver: yupResolver(schema),
    defaultValues,
  })

  // `members_preview` only carries the first 6 — fetch the full membership once, when the
  // preview is known to be incomplete, and replace the form's initial selection with it.
  const needsFullMembers = group.members_count > group.members_preview.length

  const {
    data: fullMembersData,
    isFetching: isFetchingMembers,
    isError: isMembersError,
    hasNextPage: hasNextMembersPage,
    isFetchingNextPage: isFetchingNextMembersPage,
    fetchNextPage: fetchNextMembersPage,
    refetch: refetchMembers,
  } = useInfiniteQueryGroupMembers({
    classId,
    groupId: group.id,
    options: { enabled: open && needsFullMembers },
  })

  // A failed page stops the chain; the user retries it from the error alert.
  useEffect(() => {
    if (hasNextMembersPage && !isFetchingNextMembersPage && !isMembersError) {
      fetchNextMembersPage()
    }
  }, [
    hasNextMembersPage,
    isFetchingNextMembersPage,
    isMembersError,
    fetchNextMembersPage,
  ])

  const retryMembers = () => {
    if (hasNextMembersPage) {
      fetchNextMembersPage()
    } else {
      refetchMembers()
    }
  }

  const isFetchingFullMembers =
    needsFullMembers && (!fullMembersData || hasNextMembersPage)

  // The members field stays disabled until this runs, so there are no edits to keep.
  // Hydrate once per group so a later refetch can't overwrite the user's selection.
  const hydratedGroupIdRef = useRef<number | null>(null)

  useEffect(() => {
    if (!fullMembersData || hasNextMembersPage) return
    if (hydratedGroupIdRef.current === group.id) return
    hydratedGroupIdRef.current = group.id
    methods.setValue(
      "members",
      fullMembersData.pages.flatMap((page) =>
        page.data.objects.map((member) => ({
          id: member.id,
          first_name: member.first_name ?? "",
          last_name: member.last_name ?? "",
        })),
      ),
      { shouldDirty: false },
    )
  }, [fullMembersData, hasNextMembersPage, group.id, methods.setValue])

  const { data, isFetching, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteQueryGroupCandidates({
      classId,
      groupId: group.id,
      filter: search,
      options: { enabled: open && !!classId, keepPreviousData: true },
    })
  const options =
    data?.pages.flatMap((page) =>
      page.data.objects.map((member) => ({
        id: member.id,
        first_name: member.first_name ?? "",
        last_name: member.last_name ?? "",
      })),
    ) ?? []

  // Fetch from the observer callback, not an effect on `inView`: each new sentinel
  // reports its own visibility, so loading continues while it stays in view, and a
  // stale `inView` from the previous sentinel can't fire an extra page. A failed page
  // is retried only when the sentinel scrolls back into view.
  const { ref: loadMoreRef } = useInView({
    onChange: (inView) => {
      if (inView && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
  })

  const debouncedSetSearch = useCallback(debounce(setSearch, 300), [])

  const { mutate, isLoading } = useMutationUpdateGroup({
    options: {
      onSuccess: () => {
        showSnackbar({ title: t("group.groupUpdatedSuccess") })
        handleClose()
      },
      onError: (error) => {
        const { onNameField, message, isRaw, nameError } =
          getGroupMutationFeedback(error)
        if (onNameField && nameError) {
          methods.setError("name", nameError)
          return
        }
        showSnackbar({
          title: isRaw ? message : t(message),
          variant: "error",
        })
      },
    },
  })

  const handleClose = () => {
    methods.reset(defaultValues)
    setSearch("")
    setIsMembersFocused(false)
    onClose()
  }

  const onSubmit = methods.handleSubmit((values) => {
    if (isLoading) return
    mutate({
      classId,
      groupId: group.id,
      data: {
        name: values.name,
        student_ids: values.members.map((member) => member.id),
      },
    })
  })

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      titleText={t("group.editGroup")}
      tabletMaxWidth="440px"
      actionAcceptText={t("save")}
      isLoading={isLoading}
      actionAcceptButtonProps={{ disabled: isLoading || isFetchingFullMembers }}
      onActionButtonClick={onSubmit}
    >
      <FormProvider {...methods}>
        <Stack gap="24px" component="form" onSubmit={onSubmit}>
          <TextField
            name="name"
            label={t("group.groupNameLabel")}
            placeholder={t("group.groupNameLabel")}
            fullWidth
          />
          <Controller
            name="members"
            control={methods.control}
            render={({ field: { value, onChange } }) => (
              <Autocomplete
                multiple
                disableCloseOnSelect
                disabled={isFetchingFullMembers}
                loading={isFetching}
                options={options}
                value={value}
                ListboxProps={{
                  sx: { maxHeight: listboxMaxHeight },
                }}
                onOpen={onListboxOpen}
                onClose={onListboxClose}
                onFocus={() => setIsMembersFocused(true)}
                onBlur={() => setIsMembersFocused(false)}
                onChange={(_, newValue) => onChange(newValue)}
                onInputChange={(_, newInputValue) =>
                  debouncedSetSearch(newInputValue)
                }
                isOptionEqualToValue={(option, val) => option.id === val.id}
                getOptionLabel={(option) =>
                  `${option.first_name} ${option.last_name}`
                }
                renderOption={(props, option, { selected }) => {
                  const { key: _key, ...optionProps } = props
                  const isLastOption =
                    option.id === options[options.length - 1]?.id
                  return (
                    <Fragment key={option.id}>
                      <Box component="li" {...optionProps}>
                        <Checkbox checked={selected} sx={{ mr: "8px" }} />
                        {option.first_name} {option.last_name}
                      </Box>
                      {isLastOption && hasNextPage && <Box ref={loadMoreRef} />}
                    </Fragment>
                  )
                }}
                renderTags={(members, getTagProps) =>
                  renderGroupMemberTags(isMembersFocused, members, getTagProps)
                }
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    label={t("group.searchStudentLabel")}
                    placeholder={
                      value.length === 0
                        ? t("group.searchStudentLabel")
                        : undefined
                    }
                  />
                )}
              />
            )}
          />
          {isMembersError && !isFetchingMembers && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={retryMembers}>
                  {t("group.retry")}
                </Button>
              }
            >
              {t("group.membersLoadError")}
            </Alert>
          )}
        </Stack>
      </FormProvider>
    </Dialog>
  )
}

export default EditGroupDialog
